/* tslint:disable */
/* eslint-disable */
/**
 * iEduBiofoundry API
 * **Namespacing.** Routes `/api/learn/_*`, tables `learn_*`, Python module `app/learning/`, Python classes prefixed `Learn*`. Within this namespace, \"Session\" means a *learning* Session — a facilitator-created event — never the research workspace in `app/models.py`.  **Three principals, never overlapping:**   * Facilitator — existing oauth2-proxy cookie or `X-API-Key`.   * Learner — `X-Learner-Token`, Visit-scoped, short TTL, heartbeat-renewed. Can write.   * Take-home — capability URL `/learn/me/{token}`. Read-only.  **Game data is opaque.** `LearnAttempt.state` and `LearnArtifact.payload` are arbitrary JSON owned by the frontend. The backend stores and returns them without inspection.  **The two learner tokens are stored differently, on purpose.** The visit token is hashed — it writes, it is short-lived, nobody needs it back. The take-home token is stored recoverably and is only seven characters, because it is read-only and is typed by hand off a printed card; the trade is spelled out in `API_DRAFT.md` §1 and D16.  **Call-by-call frontend walkthrough:** `API_WALKTHROUGH.md` — one Session, several Visits, every exchange in order, including the failure modes.  **Status.** Hand-authored ahead of implementation. Once `app/learning/` exists, FastAPI\'s generated schema becomes the source of truth and this file should be regenerated from it. 
 *
 * The version of the OpenAPI document: 0.1.0-draft
 * Contact: mjberry@illinois.edu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues, parseDate, parseDateTime, serializeDate, serializeDateTime } from '../runtime';
import type { Participant } from './Participant';
import {
    ParticipantFromJSON,
    ParticipantFromJSONTyped,
    ParticipantToJSON,
    ParticipantToJSONTyped,
} from './Participant';
import type { VisitStatus } from './VisitStatus';
import {
    VisitStatusFromJSON,
    VisitStatusFromJSONTyped,
    VisitStatusToJSON,
    VisitStatusToJSONTyped,
} from './VisitStatus';

/**
 * 
 * @export
 * @interface LearnVisit
 */
export interface LearnVisit {
    /**
     * 
     */
    id: string;
    /**
     * Null for solo "play alone", which has no session and therefore no session budget.
     */
    sessionId: string | null;
    /**
     * 
     */
    participants: Array<Participant>;
    /**
     * 
     */
    teamName?: string | null;
    /**
     * 
     */
    status: VisitStatus;
    /**
     * 
     */
    startedAt: Date;
    /**
     * 
     */
    lastSeenAt: Date;
    /**
     * 
     */
    endedAt?: Date | null;
    /**
     * Set when the visit ends: **one year, uniformly**, however it ended — learner `end`,
     * facilitator `close`, or the idle sweep. There is no claimed/unclaimed distinction and
     * no retrieval tracking, which is what keeps `GET /learn/me/{token}` free of side
     * effects and therefore replica-eligible (see `API_DRAFT.md` §6).
     * 
     * A visit holding **zero attempts and zero artifacts** is hard-deleted when it ends
     * instead, so it never gets a `purge_after` at all. That applies to all three ending
     * paths, not just the sweep.
     * 
     */
    purgeAfter?: Date | null;
}



/**
 * Check if a given object implements the LearnVisit interface.
 */
export function instanceOfLearnVisit(value: object): value is LearnVisit {
    if (!('id' in value) || value['id'] === undefined) return false;
    if ((!('sessionId' in (value as Record<string, any>)) && !('session_id' in (value as Record<string, any>))) || ((value as Record<string, any>)['sessionId'] === undefined && (value as Record<string, any>)['session_id'] === undefined)) return false;
    if (!('participants' in value) || value['participants'] === undefined) return false;
    if (!('status' in value) || value['status'] === undefined) return false;
    if ((!('startedAt' in (value as Record<string, any>)) && !('started_at' in (value as Record<string, any>))) || ((value as Record<string, any>)['startedAt'] === undefined && (value as Record<string, any>)['started_at'] === undefined)) return false;
    if ((!('lastSeenAt' in (value as Record<string, any>)) && !('last_seen_at' in (value as Record<string, any>))) || ((value as Record<string, any>)['lastSeenAt'] === undefined && (value as Record<string, any>)['last_seen_at'] === undefined)) return false;
    return true;
}

export function LearnVisitFromJSON(json: any): LearnVisit {
    return LearnVisitFromJSONTyped(json, false);
}

export function LearnVisitFromJSONTyped(json: any, ignoreDiscriminator: boolean): LearnVisit {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'sessionId': json['session_id'],
        'participants': ((json['participants'] as Array<any>).map(ParticipantFromJSON)),
        'teamName': json['team_name'] === undefined ? undefined : json['team_name'] === null ? null : json['team_name'],
        'status': VisitStatusFromJSON(json['status']),
        'startedAt': (json['started_at'] == null ? json['started_at'] : parseDateTime(json['started_at'])),
        'lastSeenAt': (json['last_seen_at'] == null ? json['last_seen_at'] : parseDateTime(json['last_seen_at'])),
        'endedAt': json['ended_at'] === undefined ? undefined : json['ended_at'] === null ? null : (parseDateTime(json['ended_at'])),
        'purgeAfter': json['purge_after'] === undefined ? undefined : json['purge_after'] === null ? null : (parseDateTime(json['purge_after'])),
    };
}

export function LearnVisitToJSON(json: any): LearnVisit {
    return LearnVisitToJSONTyped(json, false);
}

export function LearnVisitToJSONTyped(value?: LearnVisit | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'id': value['id'],
        'session_id': value['sessionId'],
        'participants': ((value['participants'] as Array<any>).map(ParticipantToJSON)),
        'team_name': value['teamName'],
        'status': VisitStatusToJSON(value['status']),
        'started_at': value['startedAt'] == null ? value['startedAt'] : serializeDateTime(value['startedAt']),
        'last_seen_at': value['lastSeenAt'] == null ? value['lastSeenAt'] : serializeDateTime(value['lastSeenAt']),
        'ended_at': value['endedAt'] == null ? value['endedAt'] : serializeDateTime(value['endedAt']),
        'purge_after': value['purgeAfter'] == null ? value['purgeAfter'] : serializeDateTime(value['purgeAfter']),
    };
}

